import React from 'react';
import { createPortal } from 'react-dom';
import { ErrorState } from './ErrorState';
import { useEscapeToClose } from './useEscapeToClose';

/**
 * Centred dialog mounted on `document.body`.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {string} props.title
 * @param {'sm'|'md'|'lg'} [props.size='md']
 * @param {string} [props.testId='modal'] - Handle for end-to-end tests. The
 *   dialog renders in a portal, so a test cannot reach it by looking inside the
 *   screen that opened it: it has to identify the dialog on its own.
 * @param {boolean} [props.busy=false] - Something is being saved: neither the
 *   overlay nor Escape closes the dialog, so the outcome is not lost with it.
 * @param {string|null} [props.error] - Why the last action failed, shown inside
 *   the dialog. A page banner would sit under the overlay, out of sight.
 * @param {string} [props.errorTitle='No se pudo completar']
 * @param {'center'|'sheet'} [props.placement='center'] - `sheet` rises from the
 *   bottom edge on a phone, where the thumb is, and centres from `sm` up.
 * @param {React.ReactNode} props.children
 */
export const Modal = ({
  isOpen,
  onClose,
  title,
  size = 'md',
  testId = 'modal',
  busy = false,
  error = null,
  errorTitle = 'No se pudo completar',
  placement = 'center',
  children,
}) => {
  // Before the early return on purpose: a hook that only runs on some renders
  // changes the hook order between them, and React throws.
  const titleId = React.useId();
  useEscapeToClose(isOpen, onClose, busy);

  if (!isOpen) return null;

  const maxWidth = { sm: 'max-w-sm', md: 'max-w-lg', lg: 'max-w-2xl' }[size];
  const sheet = placement === 'sheet';

  const modalContent = (
    <div
      className={`fixed inset-0 z-[9999] flex justify-center ${
        sheet ? 'items-end sm:items-center sm:p-4' : 'items-center p-4'
      }`}
    >
      {/* Overlay. A sheet skips the blur: over a live camera it is slow on a cheap phone. */}
      <div
        className={`absolute inset-0 bg-black/60 transition-opacity duration-300 ${sheet ? '' : 'backdrop-blur-sm'}`}
        onClick={busy ? undefined : onClose}
      />

      {/* Never taller than the screen: a long form scrolls inside, under a
          title that stays put, instead of spilling past both edges. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-busy={busy}
        data-testid={testId}
        data-placement={placement}
        className={`relative flex flex-col bg-white dark:bg-[#1a1a1a] shadow-2xl w-full ${maxWidth} ${
          sheet
            ? 'max-h-[92dvh] sm:max-h-full rounded-t-3xl sm:rounded-3xl pt-6 pb-[calc(1.5rem_+_env(safe-area-inset-bottom))] sm:py-8 animate-in fade-in slide-in-from-bottom-4 duration-200'
            : 'max-h-full rounded-3xl py-8 animate-in fade-in zoom-in-95 duration-200'
        }`}
      >
        <h2
          id={titleId}
          className={`shrink-0 text-2xl font-bold text-ninja-dark dark:text-white text-center ${
            sheet ? 'px-5 sm:px-8 mb-6 sm:mb-8' : 'px-8 mb-8'
          }`}
        >
          {title}
        </h2>

        <div className={`min-h-0 overflow-y-auto ${sheet ? 'px-5 sm:px-8' : 'px-8'}`}>
          {error && (
            <div className="mb-6" data-testid={`${testId}-error`}>
              <ErrorState variant="inline" title={errorTitle} description={error} />
            </div>
          )}

          {children}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
